/**
 * ServiceRegistry - Central registry for all service instances
 * Provides dependency injection and service lifecycle management
 */

import { BaseApiClient } from './BaseApiClient';
import { serviceConfig } from './ServiceConfig';
import { authProvider } from './AuthProvider';

export class ServiceRegistry {
  constructor(config = null, authProviderInstance = null) {
    this.config = config || serviceConfig.getConfig();
    this.authProvider = authProviderInstance || authProvider;
    this.services = new Map();
    this.apiClients = new Map();

    this._initialized = false;
    this._initPromise = null;
  }

  /**
   * Initialize all services
   */
  async initialize() {
    if (this._initialized) {
      return;
    }

    if (this._initPromise) {
      return this._initPromise;
    }

    this._initPromise = this._doInitialize();
    return this._initPromise;
  }

  /**
   * Perform actual initialization
   */
  async _doInitialize() {
    try {
      console.log('🔧 Initializing ServiceRegistry...');

      // Create API clients for each service
      await this._createApiClients();

      // Initialize services (lazy loading)
      console.log('✅ ServiceRegistry initialized successfully');
      this._initialized = true;

    } catch (error) {
      console.error('❌ Failed to initialize ServiceRegistry:', error);
      throw error;
    }
  }

  /**
   * Create API clients for all services
   */
  async _createApiClients() {
    const services = ['influencer', 'images'];

    for (const serviceName of services) {
      const serviceConfig = this.config[serviceName];

      const apiClient = new BaseApiClient({
        serviceName: serviceConfig.serviceName,
        baseURL: serviceConfig.baseURL,
        authProvider: this.authProvider,
        headers: serviceConfig.headers || {},
        timeout: serviceConfig.timeout || 30000,
        retry: serviceConfig.retry || { attempts: 3, delay: 1000 }
      });

      this.apiClients.set(serviceName, apiClient);
      console.log(`🔗 Created API client for ${serviceName} service: ${serviceConfig.baseURL}`);
    }
  }

  /**
   * Get or create a service instance
   */
  async getService(serviceName) {
    await this.initialize();

    // Return cached service if exists
    if (this.services.has(serviceName)) {
      return this.services.get(serviceName);
    }

    // Create new service instance
    const service = await this._createService(serviceName);
    this.services.set(serviceName, service);

    return service;
  }

  /**
   * Create a new service instance
   */
  async _createService(serviceName) {
    const apiClient = this.apiClients.get(serviceName);
    if (!apiClient) {
      throw new Error(`API client not found for service: ${serviceName}`);
    }

    const serviceConfig = this.config[serviceName];

    switch (serviceName) {
      case 'influencer':
        const { InfluencerService } = await import('../influencers/InfluencerService');
        return new InfluencerService(apiClient, serviceConfig);

      case 'images':
        const { ImageService } = await import('../images/ImageService');
        return new ImageService(apiClient, serviceConfig);

      // Processing service removed - now using direct RunPod integration
      // Training service removed - now using static TrainingService from api/trainingService

      default:
        throw new Error(`Unknown service: ${serviceName}`);
    }
  }

  /**
   * Convenience getters for services
   */
  async getInfluencerService() {
    return this.getService('influencer');
  }

  async getImageService() {
    return this.getService('images');
  }

  // Processing service removed - use processingService from '../processing/processingService'
  // Training service removed - use static TrainingService from '../api/trainingService'

  /**
   * Get all services at once
   */
  async getAllServices() {
    await this.initialize();

    const [influencer, images] = await Promise.all([
      this.getService('influencer'),
      this.getService('images')
    ]);

    return {
      influencer,
      images
    };
  }

  /**
   * Update auth provider for all services
   */
  setAuthProvider(newAuthProvider) {
    this.authProvider = newAuthProvider;

    // Update all existing API clients
    this.apiClients.forEach(client => {
      client.setAuthProvider(newAuthProvider);
    });

    console.log('🔑 Updated auth provider for all services');
  }

  /**
   * Perform health check on all services
   */
  async healthCheck() {
    await this.initialize();

    const healthChecks = Array.from(this.apiClients.entries()).map(async ([serviceName, client]) => {
      try {
        const result = await client.healthCheck();
        return result;
      } catch (error) {
        return {
          service: serviceName,
          status: 'unhealthy',
          error: error.message
        };
      }
    });

    const results = await Promise.allSettled(healthChecks);

    return results.map(result =>
      result.status === 'fulfilled' ? result.value : {
        service: 'unknown',
        status: 'error',
        error: result.reason.message
      }
    );
  }

  /**
   * Clear all services (for logout)
   */
  clear() {
    this.services.clear();
    this.authProvider.clear();
    console.log('🧹 Cleared all services');
  }

  /**
   * Get configuration information
   */
  getConfigInfo() {
    return {
      mode: this.config.mode,
      services: Object.keys(this.config).filter(key => typeof this.config[key] === 'object' && this.config[key].baseURL),
      initialized: this._initialized
    };
  }

  /**
   * Reload configuration and reinitialize services
   */
  async reload() {
    console.log('🔄 Reloading ServiceRegistry...');

    this._initialized = false;
    this._initPromise = null;
    this.services.clear();
    this.apiClients.clear();

    // Reload configuration
    this.config = serviceConfig.getConfig();

    await this.initialize();
    console.log('✅ ServiceRegistry reloaded');
  }
}

// Export singleton instance
export const serviceRegistry = new ServiceRegistry();
export default serviceRegistry;